import React, { useState, useEffect } from 'react';
import { Stack, Button, Text, Group, Badge } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import type { CarouselConfig, PanelContentWithDuration } from '../../data/DeviceConfig';
import CarouselItemEditor from './CarouselItemEditor';

interface CarouselEditorProps {
    label: string;
    items: CarouselConfig;
    onChange: (items: CarouselConfig) => void;
}

const DEFAULT_ITEM: PanelContentWithDuration = {
    component: 'DraftKingsLogoBanner',
    params: null,
    durationSeconds: 10,
};

const CarouselEditor: React.FC<CarouselEditorProps> = ({ label, items, onChange }) => {
    // Stable IDs ensure React reuses component instances correctly when items are reordered,
    // preventing local state (like UTM pair inputs) from mismatching their items.
    const [itemIds, setItemIds] = useState<string[]>(() =>
        items.map(() => crypto.randomUUID())
    );

    // Sync IDs when items are loaded from an external source (e.g. API response)
    useEffect(() => {
        setItemIds(prev => {
            if (prev.length === items.length) return prev;
            return items.map((_, i) => prev[i] ?? crypto.randomUUID());
        });
    }, [items.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleItemChange = (index: number, item: PanelContentWithDuration) => {
        const next = [...items];
        next[index] = item;
        onChange(next);
    };

    const handleRemove = (index: number) => {
        setItemIds(prev => prev.filter((_, i) => i !== index));
        onChange(items.filter((_, i) => i !== index));
    };

    const handleMoveUp = (index: number) => {
        if (index === 0) return;
        const nextIds = [...itemIds];
        [nextIds[index - 1], nextIds[index]] = [nextIds[index], nextIds[index - 1]];
        setItemIds(nextIds);
        const next = [...items];
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
        onChange(next);
    };

    const handleMoveDown = (index: number) => {
        if (index === items.length - 1) return;
        const nextIds = [...itemIds];
        [nextIds[index], nextIds[index + 1]] = [nextIds[index + 1], nextIds[index]];
        setItemIds(nextIds);
        const next = [...items];
        [next[index], next[index + 1]] = [next[index + 1], next[index]];
        onChange(next);
    };

    const handleAdd = () => {
        setItemIds(prev => [...prev, crypto.randomUUID()]);
        onChange([...items, { ...DEFAULT_ITEM }]);
    };

    return (
        <Stack gap="sm">
            <Group gap="xs">
                <Text fw={500} size="sm">
                    {label}
                </Text>
                <Badge size="sm" variant="light" color="gray">
                    {items.length}
                </Badge>
            </Group>

            {items.length === 0 && (
                <Text size="sm" c="dimmed">
                    No components configured. Add one below.
                </Text>
            )}

            {items.map((item, index) => (
                <CarouselItemEditor
                    key={itemIds[index]}
                    item={item}
                    index={index}
                    totalItems={items.length}
                    onChange={updated => handleItemChange(index, updated)}
                    onRemove={() => handleRemove(index)}
                    onMoveUp={() => handleMoveUp(index)}
                    onMoveDown={() => handleMoveDown(index)}
                />
            ))}

            <Button
                variant="light"
                size="sm"
                leftSection={<IconPlus size={14} />}
                onClick={handleAdd}
                style={{ alignSelf: 'flex-start' }}
            >
                Add Component
            </Button>
        </Stack>
    );
};

export default CarouselEditor;
